import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import axios from "../../api";
import {
  Card,
  Button,
  TextField,
  FormControl,
  Grid,
  Box,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";
import type { RootState } from "store";
import { formatMoney } from "utils/money";
import {
  waitForSubscriptionPaymentStatus,
  SUBSCRIPTION_PAYMENT_PENDING_MESSAGE,
} from "utils/waitForSubscriptionPaymentStatus";

const todayDate = new Date().toISOString().split("T")[0];

const validationSchema = Yup.object({
  customer_id: Yup.string().required("User is required"),
  asset_id: Yup.string().required("Asset is required"),
  asset_type_id: Yup.string().required("Asset type is required"),
  subscription_type_id: Yup.string().required("Subscription type is required"),
  start_date: Yup.string()
    .matches(
      /^\d{4}-\d{2}-\d{2}$/,
      "Start date must be in the format YYYY-MM-DD"
    )
    .required("Start date is required")
    .test("is-valid-date", "Start date is not a valid date", (value) => {
      return !Number.isNaN(new Date(value).getTime());
    }),
  end_date: Yup.string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, "End date must be in the format YYYY-MM-DD")
    .required("End date is required")
    .test("is-valid-date", "End date is not a valid date", (value) => {
      return !Number.isNaN(new Date(value).getTime());
    })
    .test(
      "is-greater-than-today",
      "End date must be greater than today",
      (value) => {
        if (!value) return true;
        const endDate = new Date(value);
        return endDate > new Date(todayDate);
      }
    ),
});

interface AssetType {
  asset_type_id: number;
  asset_type_name: string;
  is_active?: number | boolean;
}

interface Asset {
  asset_id: number;
  asset_name: string;
  is_active?: number | boolean;
}

interface Customers {
  customer_id: number;
  name: string;
  email?: string;
  type: string;
  is_active?: number | boolean;
}

interface Subscription {
  subscription_type_id: number;
  subscription_type_name: string;
  duration_in_days: number;
  is_active?: number | boolean;
}

interface Division {
  division_id: number;
  division_name: string;
}

type FormValues = {
  customer_id: string;
  subscription_type_id: string | number | null;
  start_date: string;
  end_date: string;
  asset_id: string;
  division_id: number | null;
  asset_type_id: string;
  lift_id: number | null;
};

interface PricePreview {
  final_price: number;
  original_price: number;
  custom_plan_id: number | null;
  effective_duration_days: number | null;
  currency: string;
  gst_percent?: number;
  gst_amount?: number;
  total_amount?: number;
}

interface AutoFillParams {
  initialCustomerId?: number;
  selectedCustomer: number | null;
  assetTypesData: AssetType[];
  assetOptions: Asset[];
  values: FormValues;
  setFieldValue: (field: string, value: unknown) => void;
  hasAutoFilledAsset: React.MutableRefObject<boolean>;
  setSelectedAssetType: (value: AssetType | null) => void;
  setSelectedAsset: (value: Asset | null) => void;
}

const syncCalculatedEndDateField = (
  calculatedEndDate: string,
  values: FormValues,
  setFieldValue: (field: string, value: unknown) => void
) => {
  if (calculatedEndDate && values.end_date !== calculatedEndDate) {
    setFieldValue("end_date", calculatedEndDate);
  }
};

const autoFillSingleAsset = ({
  initialCustomerId,
  selectedCustomer,
  assetTypesData,
  assetOptions,
  values,
  setFieldValue,
  hasAutoFilledAsset,
  setSelectedAssetType,
  setSelectedAsset,
}: AutoFillParams) => {
  if (
    initialCustomerId &&
    selectedCustomer &&
    assetTypesData.length === 1 &&
    assetOptions.length === 1 &&
    !values.asset_id &&
    !hasAutoFilledAsset.current
  ) {
    hasAutoFilledAsset.current = true;
    const at = assetTypesData[0];
    const asset = assetOptions[0];
    setFieldValue("asset_type_id", at?.asset_type_id ?? "");
    setFieldValue("asset_id", asset?.asset_id ?? "");
    setSelectedAssetType(at);
    setSelectedAsset(asset);
  }
};

const buildPricePreviewLabel = (
  isResolvingPrice: boolean,
  pricePreview: PricePreview | null,
  appCurrency: string
): string | null => {
  if (isResolvingPrice) return "Checking price...";
  if (!pricePreview) return null;
  const currency = pricePreview.currency ?? appCurrency;
  const finalPrice = formatMoney(
    Number(pricePreview.final_price ?? 0),
    String(currency)
  );
  if (pricePreview.custom_plan_id != null) {
    const listPrice = formatMoney(
      Number(pricePreview.original_price ?? 0),
      String(currency)
    );
    return `Custom plan — pay ${finalPrice} (list ${listPrice})`;
  }
  return `Amount due: ${finalPrice}`;
};

const ensureErrorObject = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export interface AddSubscriptionProps {
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** When set (e.g. from customer Subscriptions overlay), customer is pre-filled; if only one asset, asset type and asset are also pre-filled */
  initialCustomerId?: number;
}

const useAddSubscriptionLogic = ({
  embedded,
  onSuccess,
  onCancel,
  initialCustomerId,
}: AddSubscriptionProps = {}) => {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const appCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";
  const { fetchError, fetchSuccess } = useSnackbar();
  const [users, setUsers] = useState<Customers[]>([]);
  const [assetTypesData, setAssetTypesData] = useState<AssetType[]>([]);
  const [subscriptionTypes, setSubscriptionTypes] = useState<Subscription[]>(
    []
  );
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState<Subscription | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [divisionAssets, setDivisionAssets] = useState<Asset[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(initialCustomerId ?? null);
  const [selectedCustomerType, setSelectedCustomerType] = useState<
    string | null
  >(null);

  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const [selectedAssetType, setSelectedAssetType] = useState<AssetType | null>(
    null
  );
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<Division | null>(
    null
  );
  const [isPaying, setIsPaying] = useState(false);

  // State for managing start and end dates
  const [startDateValue, setStartDateValue] = useState<string>("");
  const [calculatedEndDate, setCalculatedEndDate] = useState<string>("");
  const hasAutoFilledAsset = useRef(false);

  // Phase 4 / Screen 3: resolve price for the selected (customer, asset, subscription type)
  // so we can show a preview and also ensure custom_plan_id/original_amount are included in the payment creation.
  const [pricePreview, setPricePreview] = useState<PricePreview | null>(null);
  const [isResolvingPrice, setIsResolvingPrice] = useState(false);

  const [payConfirm, setPayConfirm] = useState<null | {
    values: FormValues;
  }>(null);

  type FutureSubscriptionConfirmDetail = {
    error: string;
    message?: string;
    existing: { start_date: string; end_date: string };
    proposed: { start_date: string; end_date: string };
  };
  const [futureConfirmDialog, setFutureConfirmDialog] = useState<null | {
    formValues: FormValues;
    detail: FutureSubscriptionConfirmDetail;
  }>(null);

  const formatSubscriptionDate = (ymd: string) => {
    try {
      return new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return ymd;
    }
  };

  const loadRazorpayScript = useCallback((): Promise<void> => {
    if (typeof window !== "undefined" && (window as any).Razorpay) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });
  }, []);

  useEffect(() => {
    if (initialCustomerId && users.length) {
      const user = users.find((u) => u.customer_id === initialCustomerId);
      if (user) {
        setSelectedCustomer(initialCustomerId);
        setSelectedCustomerType(user.type ?? "customer");
      }
    }
  }, [initialCustomerId, users]);

  const fetchUsersData = async () => {
    try {
      const response = await axios.get("/customer", { params: { is_active: 1 } });
      const activeCustomers = (response?.data?.list || []).filter(
        (entry: Customers) =>
          entry?.is_active === 1 || entry?.is_active === true
      );
      setUsers(activeCustomers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchAssetsData = useCallback(async () => {
    try {
      const assetTypesData = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });
      setAssetTypesData(assetTypesData?.data?.list || []);
    } catch (error) {
      console.error("Error fetching asset types:", error);
    }
  }, []);

  useEffect(() => {
    fetchAssetsData();
  }, [fetchAssetsData]);

  const fetchAssets = useCallback(async () => {
    if (selectedCustomer && selectedAssetType?.asset_type_id) {
      try {
        const response = await axios.get(
          `/assets/${selectedAssetType?.asset_type_id}/${selectedCustomer}`
        );
        const activeAssets = (response?.data?.list || []).filter(
          (entry: Asset) => Number(entry?.is_active ?? 1) === 1
        );
        setAssets(activeAssets);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    }
  }, [selectedCustomer, selectedAssetType?.asset_type_id]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const fetchAssetsByDivisionsAndAssetTypes = useCallback(async () => {
    if (
      selectedCustomer &&
      selectedAssetType?.asset_type_id &&
      selectedDivisions?.division_id
    ) {
      try {
        const response = await axios.get(
          `/assets/${selectedDivisions?.division_id}/${selectedAssetType?.asset_type_id}/${selectedCustomer}`
        );
        const activeAssets = (response?.data?.list || []).filter(
          (entry: Asset) => Number(entry?.is_active ?? 1) === 1
        );
        setDivisionAssets(activeAssets);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    }
  }, [
    selectedCustomer,
    selectedAssetType?.asset_type_id,
    selectedDivisions?.division_id,
  ]);

  useEffect(() => {
    if (!selectedCustomer || !selectedAssetType?.asset_type_id) return;

    if (selectedCustomerType === "customer") {
      fetchAssets();
    } else if (
      selectedCustomerType === "company" &&
      selectedDivisions?.division_id
    ) {
      fetchAssetsByDivisionsAndAssetTypes();
    }
  }, [
    selectedCustomer,
    selectedAssetType?.asset_type_id,
    selectedCustomerType,
    selectedDivisions?.division_id,
    fetchAssets,
    fetchAssetsByDivisionsAndAssetTypes,
  ]);

  const fetchSubsciptionTypes = useCallback(async () => {
    try {
      const subscriptionTypes = await axios.get(
        `/subscription-types/assets/${selectedAssetType?.asset_type_id}`,
        {
          params: { is_active: 1 },
        }
      );
      const activeTypes = (subscriptionTypes?.data?.list || []).filter(
        (entry: Subscription) => Number(entry?.is_active ?? 1) === 1
      );
      setSubscriptionTypes(activeTypes);
    } catch (error) {
      console.error("Error fetching asset types:", error);
    }
  }, [selectedAssetType?.asset_type_id]);

  useEffect(() => {
    fetchSubsciptionTypes();
  }, [fetchSubsciptionTypes]);

  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  const fetchDivisions = useCallback(async () => {
    try {
      const response = await axios.get(
        `/divisions/customer/${selectedCustomer}`
      );
      setDivisions(response?.data?.divisions);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  }, [selectedCustomer]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  const assetOptions =
    selectedCustomerType === "company" ? divisionAssets : assets;

  const pricePreviewLabel = useMemo(
    () => buildPricePreviewLabel(isResolvingPrice, pricePreview, appCurrency),
    [isResolvingPrice, pricePreview, appCurrency]
  );

  const fetchPricePreview = useCallback(
    async (state: { cancelled: boolean }) => {
      const customerId = selectedCustomer;
      const assetId = selectedAsset?.asset_id;
      const subscriptionTypeId = selectedSubscriptionType?.subscription_type_id;

      if (!customerId || !assetId || !subscriptionTypeId) {
        if (!state.cancelled) {
          setPricePreview(null);
          setIsResolvingPrice(false);
        }
        return;
      }

      setIsResolvingPrice(true);
      try {
        const res = await axios.post("/customer-subscriptions/resolve-price", {
          asset_id: assetId,
          subscription_type_id: subscriptionTypeId,
          customer_id: customerId,
        });

        if (state.cancelled) return;

        setPricePreview({
          final_price: Number(res.data?.final_price ?? 0),
          original_price: Number(res.data?.original_price ?? 0),
          custom_plan_id: res.data?.custom_plan_id ?? null,
          effective_duration_days: res.data?.effective_duration_days ?? null,
          currency: res.data?.currency ?? appCurrency,
          gst_percent: res.data?.gst_percent ?? 0,
          gst_amount: Number(res.data?.gst_amount ?? 0),
          total_amount: res.data?.total_amount ?? null,
        });
      } catch (e) {
        if (state.cancelled) return;
        console.error("Failed to resolve price preview:", e);
        setPricePreview(null);
      } finally {
        if (!state.cancelled) {
          setIsResolvingPrice(false);
        }
      }
    },
    [
      selectedCustomer,
      selectedAsset?.asset_id,
      selectedSubscriptionType?.subscription_type_id,
      appCurrency,
    ]
  );

  useEffect(() => {
    const state = { cancelled: false };
    fetchPricePreview(state);
    return () => {
      state.cancelled = true;
    };
  }, [fetchPricePreview]);

  const initiatePayment = useCallback(
    async (
      values: FormValues,
      options?: { confirmFuture?: boolean }
    ): Promise<boolean> => {
      if (!selectedSubscriptionType) {
        throw new Error("Please select a subscription type");
      }

      if (!values.start_date) {
        throw new Error("Start date is required");
      }

      const assetId = Number(values.asset_id);
      const subscriptionTypeId = Number(values.subscription_type_id);
      const customerId = Number(values.customer_id);

      if (!assetId || !subscriptionTypeId || !customerId) {
        throw new Error("Please select customer, asset and subscription plan");
      }

      setIsPaying(true);
      try {
        let orderResponse;
        try {
          orderResponse = await axios.post(
            "/customer-subscriptions/create-order",
            {
              asset_id: assetId,
              subscription_type_id: subscriptionTypeId,
              customer_id: customerId,
              start_date: values.start_date,
              confirm_future_subscription: options?.confirmFuture === true,
              // Optional snapshot hints (server still re-resolves and only trusts exact matches)
              custom_plan_id: pricePreview?.custom_plan_id ?? null,
              original_amount: pricePreview?.original_price ?? null,
            }
          );
        } catch (orderErr) {
          if (
            orderErr instanceof AxiosError &&
            orderErr.response?.status === 409 &&
            orderErr.response?.data?.error === "FUTURE_SUBSCRIPTION_CONFIRM"
          ) {
            setFutureConfirmDialog({
              formValues: values,
              detail: orderErr.response.data as FutureSubscriptionConfirmDetail,
            });
            return true;
          }
          throw orderErr;
        }

        await loadRazorpayScript();
        const Razorpay = (window as any).Razorpay;
        if (!Razorpay) {
          throw new Error("Payment gateway could not be loaded.");
        }

        await new Promise<void>((resolve, reject) => {
          let isSettled = false;
          const settleSuccess = () => {
            if (isSettled) return;
            isSettled = true;
            resolve();
          };
          const settleFailure = (error: unknown) => {
            if (isSettled) return;
            isSettled = true;
            reject(ensureErrorObject(error));
          };

          const rzp = new Razorpay({
            key: orderResponse.data.key_id,
            amount: Number(orderResponse.data.amount),
            currency: orderResponse.data.currency ?? appCurrency,
            order_id: orderResponse.data.orderId,
            name: "Scout Track Solutions",
            description: `${selectedSubscriptionType.subscription_type_name} subscription`,
            handler: async (response: {
              razorpay_payment_id: string;
              razorpay_order_id: string;
              razorpay_signature: string;
            }) => {
              try {
                const verifyPayload = {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  customer_id: customerId,
                  start_date: values.start_date,
                  confirm_future_subscription: options?.confirmFuture === true,
                };
                const verifyPromise = axios.post("/customer-subscriptions/verify", verifyPayload);
                const poll = await waitForSubscriptionPaymentStatus(
                  response.razorpay_order_id,
                  "customer"
                );
                let verifyOk = false;
                try {
                  const verifyRes = await verifyPromise;
                  verifyOk = verifyRes.status === 200 || verifyRes.status === 201;
                } catch (e) {
                  console.log("[PAYMENT_DEBUG] customer (AddSubscription): verify failed", {
                    orderId: response.razorpay_order_id,
                    message: (e as Error)?.message,
                  });
                  verifyOk = false;
                }
                if (poll.activated || verifyOk) {
                  fetchSuccess("Subscription activated successfully.");
                } else {
                  fetchSuccess(SUBSCRIPTION_PAYMENT_PENDING_MESSAGE);
                }
                settleSuccess();
              } catch (verificationError) {
                settleFailure(verificationError);
              }
            },
            modal: {
              ondismiss: () => {
                settleFailure(new Error("Payment was cancelled"));
              },
            },
          });
          rzp.open();
        });
      } finally {
        setIsPaying(false);
      }
      return false;
    },
    [fetchSuccess, loadRazorpayScript, selectedSubscriptionType, pricePreview, appCurrency]
  );

  const getCustomerName = useCallback(() => {
    if (!selectedCustomer) return "";
    const row = users.find((u) => Number(u.customer_id) === Number(selectedCustomer));
    return row?.name || "";
  }, [selectedCustomer, users]);

  // Calculate end date when subscription type or start date changes
  useEffect(() => {
    const durationDays =
      pricePreview?.effective_duration_days ??
      selectedSubscriptionType?.duration_in_days ??
      0;

    if (startDateValue && durationDays > 0) {
      const start = new Date(startDateValue);
      const end = new Date(start);
      end.setDate(start.getDate() + durationDays);
      const endDate = end.toISOString().split("T")[0];
      setCalculatedEndDate(endDate);
    } else {
      setCalculatedEndDate("");
    }
  }, [selectedSubscriptionType, startDateValue, pricePreview?.effective_duration_days]);

  const handleFutureSubscriptionConfirmed = async () => {
    if (!futureConfirmDialog) return;
    const v = futureConfirmDialog.formValues;
    setFutureConfirmDialog(null);
    try {
      const stillNeeds = await initiatePayment(v, { confirmFuture: true });
      if (stillNeeds) return;
      if (embedded) {
        onSuccess?.();
      } else {
        navigate("/subscriptions-list");
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response) {
        fetchError(
          (error.response?.data as { error?: string })?.error ??
            error.message
        );
      } else if (error instanceof Error) {
        fetchError(error.message);
      } else {
        fetchError("Failed to process payment. Please try again.");
      }
    }
  };

  const content = (
    <Box sx={{ width: "100%", ...(!embedded && { p: 3, borderRadius: 2, boxShadow: 2 }) }}>
      <Dialog
        open={Boolean(futureConfirmDialog)}
        onClose={() => !isPaying && setFutureConfirmDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add subscription after current period?</DialogTitle>
        <DialogContent>
          {futureConfirmDialog
            ? futureConfirmDialog.detail.message ??
              `Do you want to add a new subscription from ${formatSubscriptionDate(
                futureConfirmDialog.detail.proposed.start_date
              )} to ${formatSubscriptionDate(
                futureConfirmDialog.detail.proposed.end_date
              )}? (Current subscription on this asset runs until ${formatSubscriptionDate(
                futureConfirmDialog.detail.existing.end_date
              )}.)`
            : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setFutureConfirmDialog(null)}
            disabled={isPaying}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isPaying}
            onClick={handleFutureSubscriptionConfirmed}
            sx={{ textTransform: "none" }}
          >
            {isPaying ? "Processing…" : "Yes, add subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      {!embedded && (
        <SoftTypography variant="h5" style={{ marginBottom: "18px" }}>
          Add New Subscription
        </SoftTypography>
      )}
      <Formik<FormValues>
          enableReinitialize
          key={initialCustomerId ?? "sub-default"}
          initialValues={{
            customer_id: initialCustomerId ? String(initialCustomerId) : "",
            subscription_type_id: null,
            start_date: "",
            end_date: "",
            asset_id: "",
            division_id: null,
            asset_type_id: "",
            lift_id: null,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            values.division_id ??= null;
            try {
              if (!pricePreview) {
                fetchError("Unable to calculate price. Please reselect the plan.");
                return;
              }
              setPayConfirm({ values });
            } catch (error: unknown) {
              if (error instanceof AxiosError && error.response) {
                fetchError(
                  (error.response?.data as { error?: string })?.error ||
                    error.message
                );
              } else if (error instanceof Error) {
                fetchError(error.message);
              } else {
                fetchError("Failed to process payment. Please try again.");
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ errors, touched, setFieldValue, values, isSubmitting }) => {
            const assetOpts =
              selectedCustomerType === "company" ? divisionAssets : assets;
            syncCalculatedEndDateField(
              calculatedEndDate,
              values,
              setFieldValue
            );
            autoFillSingleAsset({
              initialCustomerId,
              selectedCustomer,
              assetTypesData,
              assetOptions: assetOpts,
              values,
              setFieldValue,
              hasAutoFilledAsset,
              setSelectedAssetType,
              setSelectedAsset,
            });

            return (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Customer
                      </SoftTypography>
                      <Autocomplete
                        options={users || []}
                        getOptionLabel={(option) =>
                          `${option.name}${option.email ? ` - ${option.email}` : ""}`
                        }
                        value={users?.find((u) => u.customer_id === Number(values.customer_id)) ?? null}
                        onChange={(event, value) => {
                          setFieldValue(
                            "customer_id",
                            value?.customer_id || ""
                          );
                          setSelectedCustomer(value?.customer_id || null);
                          setSelectedCustomerType(value?.type || null);
                          setFieldValue("asset_type_id", "");
                          setFieldValue("asset_id", "");
                          setFieldValue("subscription_type_id", "");
                          setSelectedAsset(null);
                          setSelectedSubscriptionType(null);
                          setSelectedAssetType(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.customer_id && touched.customer_id}
                            helperText={
                              touched.customer_id && errors.customer_id
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {selectedCustomerType === "company" && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <SoftTypography variant="caption" fontWeight="bold">
                          Division
                        </SoftTypography>
                        <Autocomplete
                          options={divisions || []}
                          getOptionLabel={(option) => `${option.division_name}`}
                          value={selectedDivisions || null}
                          onChange={(event, value) => {
                            setFieldValue(
                              "division_id",
                              value ? value.division_id : null
                            );
                            setSelectedDivisions(value);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              error={
                                !!errors.division_id && touched.division_id
                              }
                              helperText={
                                touched.division_id && errors.division_id
                              }
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Asset Type
                      </SoftTypography>
                      <Autocomplete
                        options={assetTypesData || []}
                        getOptionLabel={(option) => option.asset_type_name}
                        value={selectedAssetType}
                        onChange={(event, value) => {
                          setFieldValue(
                            "asset_type_id",
                            value?.asset_type_id || ""
                          );
                          setSelectedAssetType(value);
                          setFieldValue("asset_id", "");
                          setSelectedAsset(null);
                          setFieldValue("subscription_type_id", "");
                          setSelectedSubscriptionType(null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.asset_type_id && touched.asset_type_id
                            }
                            helperText={
                              touched.asset_type_id && errors.asset_type_id
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Asset
                      </SoftTypography>
                      <Autocomplete
                        options={assetOptions || []}
                        getOptionLabel={(option) => option.asset_name}
                        value={selectedAsset}
                        onChange={(event, value) => {
                          setFieldValue("asset_id", value?.asset_id || "");
                          setSelectedAsset(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={!!errors.asset_id && touched.asset_id}
                            helperText={touched.asset_id && errors.asset_id}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Subscription Type
                      </SoftTypography>
                      <Autocomplete
                        options={subscriptionTypes || []}
                        getOptionLabel={(option) =>
                          option?.subscription_type_name
                        }
                        value={selectedSubscriptionType}
                        onChange={(event, value) => {
                          setFieldValue(
                            "subscription_type_id",
                            value?.subscription_type_id || ""
                          );
                          setSelectedSubscriptionType(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            error={
                              !!errors.subscription_type_id &&
                              touched.subscription_type_id
                            }
                            helperText={
                              touched.subscription_type_id &&
                              errors.subscription_type_id
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    {pricePreviewLabel && (
                      <SoftTypography variant="caption" fontWeight="bold">
                        {pricePreviewLabel}
                      </SoftTypography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        Start Date
                      </SoftTypography>
                      <Field
                        className="date-field"
                        name="start_date"
                        as={TextField}
                        type="date"
                        fullWidth
                        InputProps={{
                          inputProps: { min: formattedToday },
                        }}
                        variant="outlined"
                        error={errors.start_date && touched.start_date}
                        helperText={touched.start_date && errors.start_date}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = e.target.value;
                          setFieldValue("start_date", value);
                          setStartDateValue(value);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <SoftTypography variant="caption" fontWeight="bold">
                        End Date
                      </SoftTypography>
                      <Field
                        className="date-field"
                        name="end_date"
                        as={TextField}
                        type="date"
                        fullWidth
                        variant="outlined"
                        InputProps={{
                          readOnly: true,
                        }}
                        error={errors.end_date && touched.end_date}
                        helperText={touched.end_date && errors.end_date}
                      />
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      className="success-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                      }}
                      variant="contained"
                      type="submit"
                      color="success"
                      disabled={isPaying || isSubmitting}
                    >
                      {isPaying ? "Processing Payment..." : "Pay & Activate"}
                    </Button>

                    <Button
                      className="cancel-btn"
                      sx={{
                        position: "relative",
                        minWidth: 100,
                        ml: 2.5,
                      }}
                      variant="contained"
                      color="warning"
                      disabled={isPaying}
                      onClick={() => {
                        if (embedded) {
                          onCancel?.();
                        } else {
                          navigate("/subscriptions-list");
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Form>
            );
          }}
        </Formik>

      <Dialog
        open={Boolean(payConfirm)}
        onClose={() => (isPaying ? null : setPayConfirm(null))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm payment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gridTemplateColumns: "140px 1fr", rowGap: 1, columnGap: 2 }}>
            <SoftTypography variant="caption" fontWeight="bold">
              Subscription:
            </SoftTypography>
            <SoftTypography variant="caption">
              {selectedSubscriptionType?.subscription_type_name || "-"}
            </SoftTypography>

            <SoftTypography variant="caption" fontWeight="bold">
              Customer:
            </SoftTypography>
            <SoftTypography variant="caption">
              {getCustomerName() || "-"}
            </SoftTypography>

            <SoftTypography variant="caption" fontWeight="bold">
              Asset:
            </SoftTypography>
            <SoftTypography variant="caption">
              {selectedAsset?.asset_name || "-"}
            </SoftTypography>
          </Box>

          {pricePreview ? (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  rowGap: 0.75,
                  columnGap: 2,
                  mt: 1,
                }}
              >
                <SoftTypography variant="caption" fontWeight="bold">
                  Subtotal:
                </SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">
                  {formatMoney(
                    Number(pricePreview.final_price ?? 0),
                    String(pricePreview.currency ?? appCurrency)
                  )}
                </SoftTypography>

                {Number(pricePreview.gst_percent || 0) > 0 ? (
                  <>
                    <SoftTypography variant="caption" fontWeight="bold">
                      TAX ({Number(pricePreview.gst_percent || 0)}%):
                    </SoftTypography>
                    <SoftTypography variant="caption" fontWeight="bold">
                      {formatMoney(
                        Number(pricePreview.gst_amount ?? 0),
                        String(pricePreview.currency ?? appCurrency)
                      )}
                    </SoftTypography>
                  </>
                ) : null}

                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    my: 0.5,
                  }}
                />

                <SoftTypography variant="caption" fontWeight="bold">
                  Total:
                </SoftTypography>
                <SoftTypography variant="caption" fontWeight="bold">
                  {formatMoney(
                    Number(
                      pricePreview.total_amount ??
                        Number(pricePreview.final_price ?? 0) +
                          Number(pricePreview.gst_amount ?? 0)
                    ),
                    String(pricePreview.currency ?? appCurrency)
                  )}
                </SoftTypography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPayConfirm(null)}
            disabled={isPaying}
            sx={{ textTransform: "none" }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={isPaying || !payConfirm}
            sx={{ textTransform: "none" }}
            onClick={async () => {
              if (!payConfirm) return;
              try {
                setPayConfirm(null);
                const needsFutureConfirm = await initiatePayment(payConfirm.values, {
                  confirmFuture: false,
                });
                if (needsFutureConfirm) return;
                if (embedded) {
                  onSuccess?.();
                } else {
                  navigate("/subscriptions-list");
                }
              } catch (error: unknown) {
                if (error instanceof AxiosError && error.response) {
                  fetchError(
                    (error.response?.data as { error?: string })?.error ??
                      error.message
                  );
                } else if (error instanceof Error) {
                  fetchError(error.message);
                } else {
                  fetchError("Failed to process payment. Please try again.");
                }
              }
            }}
          >
            Confirm &amp; Pay
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return { content };
};

const AddSubscription = (props: AddSubscriptionProps = {}) => {
  const { content } = useAddSubscriptionLogic(props);
  if (props.embedded) {
    return content;
  }
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {content}
    </DashboardLayout>
  );
};

export default AddSubscription;
